import { CompositeTilemap } from "@pixi/tilemap"
import { BLEND_MODES, Renderer } from "pixi.js"

/**
 * A CompositeTilemap that draws with normal blending, and through its own filters.
 *
 * @pixi/tilemap never sets the blend state; it draws with whatever the previous object left, which
 * can be an additive sprite from the end of the last frame. Every tile layer was then ADDED onto the
 * ones below it, so straw over grass came out yellow-green. A filter pass just before the tile layers
 * used to reset the state by accident.
 *
 * Nor does it look at `filters`: its render draws the tiles straight to the target. Set filters are
 * pushed around that draw here, as a Container does (a roof lifted through its storey's tone,
 * MapRenderingManager.setRoofTone).
 *
 * Nor does it skip itself when empty: it flushed the batch and bound its shader, uploading every uniform
 * array, for no tiles at all. Every passive storey keeps one per tile layer, mostly empty: behind a nine-storey
 * house, 57 of the 72 tilemaps drawn each frame held nothing, and those binds were most of the frame's time.
 */
export class NormalBlendTilemap extends CompositeTilemap {
    render(renderer: Renderer): void {
        if (!this.visible || this.worldAlpha <= 0 || !this.renderable || this.isEmpty()) return
        const filters = this.filters?.filter(filter => filter.enabled)
        if (!filters?.length) {
            this.renderTiles(renderer)
            return
        }
        renderer.batch.flush()
        renderer.filter.push(this, filters)
        this.renderTiles(renderer)
        renderer.batch.flush()
        renderer.filter.pop()
    }

    /** No layer holds a tile (a layer keeps its tiles' vertices in `pointsBuf`, private in its typings). */
    private isEmpty(): boolean {
        return !this.children.some(layer => (layer as unknown as { pointsBuf?: number[] }).pointsBuf?.length)
    }

    private renderTiles(renderer: Renderer): void {
        // Flush what is already batched under its own blend modes before taking over the state.
        renderer.batch.setObjectRenderer((renderer.plugins as any).tilemap)
        renderer.state.setBlendMode(BLEND_MODES.NORMAL)
        super.render(renderer)
    }
}
